import { getWindowBaseURL } from "@/lib/get-base-url";

import type { FormTable } from "@/types/forms/form";
import type { UserTiny } from "@/types/user";

import { createFormLink } from "./create-form-link";

type FormMessagePatient = Pick<UserTiny, "firstName" | "lastName">;

/** A patient-facing form link, carrying the plan when the form fills in membership
 *  details — without it a form shared by several plans cannot tell which price to show. */
export function buildFormUrl(
  tenantSubDomain: string | undefined,
  pathName: string,
  membershipId?: string,
): string {
  const url = `${getWindowBaseURL(tenantSubDomain)}/form/${pathName}`;
  return membershipId
    ? `${url}?membershipId=${encodeURIComponent(membershipId)}`
    : url;
}

/** The same link for a suite of forms. The plan rides along here too: the suite renders
 *  the same forms as the single link, so dropping it would 409 what one form resolves. */
export function buildFormSuiteUrl(
  tenantSubDomain: string | undefined,
  pathNames: string[],
  membershipId?: string,
): string {
  const url = `${getWindowBaseURL(tenantSubDomain)}/forms?forms=${pathNames.join(",")}`;
  return membershipId
    ? `${url}&membershipId=${encodeURIComponent(membershipId)}`
    : url;
}

/**
 * Replaces common patient/form name placeholders in a template string.
 *
 * This handles build-time substitution of patient-specific tokens
 * ({first_name}, {patient.name}, {form.name}, etc.) into template text.
 * This is distinct from the email composer's render-time variable system
 * (mention nodes / {variableId} placeholders resolved by renderInlineContent),
 * which handles tenant/email variables like {{practice_name}}.
 *
 * URL placeholders ({form.url}, {url}) are NOT replaced here because
 * callers handle them differently: plain text inserts the URL directly,
 * while the email flow converts them to button nodes.
 */
export function replaceFormPlaceholders(
  text: string,
  patient: FormMessagePatient,
  formName: string,
): string {
  const fullName = `${patient.firstName} ${patient.lastName || ""}`.trim();
  return text
    .replace(/\{first_name\}/gi, patient.firstName)
    .replace(/\{last_name\}/gi, patient.lastName || "")
    .replace(/\{full_name\}/gi, fullName)
    .replace(/\{patient\.first_name\}/gi, patient.firstName)
    .replace(/\{patient\.last_name\}/gi, patient.lastName || "")
    .replace(/\{patient\.name\}/gi, fullName)
    .replace(/\{form\.name\}/gi, formName);
}

/**
 * Generates a templated message for sending a form link to a patient
 */
function renderFormMessageTemplate(
  template: string,
  patient: FormMessagePatient,
  formName: string,
  formUrl: string,
): string {
  const hasUrlPlaceholder = /\{(?:form\.)?url\}/i.test(template);
  const message = replaceFormPlaceholders(template, patient, formName)
    .replace(/\{form\.url\}/gi, formUrl)
    .replace(/\{url\}/gi, formUrl);

  // Always include the link, even when the template omits the URL placeholder.
  return hasUrlPlaceholder ? message : `${message.trimEnd()}\n\n${formUrl}`;
}

export function generateFormMessage(
  patient: FormMessagePatient,
  form: FormTable,
  tenantSubDomain?: string,
  membershipId?: string,
): string {
  const formUrl = buildFormUrl(tenantSubDomain, form.pathName, membershipId);

  if (form.defaultSendingText) {
    return renderFormMessageTemplate(
      form.defaultSendingText,
      patient,
      form.name,
      formUrl,
    );
  }

  // Default template - natural sentence format for patients
  // Check if form name already contains "Form" to avoid duplication
  const formText = form.name.toLowerCase().includes("form")
    ? form.name
    : `${form.name} form`;

  return `Hi ${patient.firstName}, please fill out the ${formText}.\n\n${formUrl}`;
}

/**
 * Generates a templated message for sending multiple forms to a patient
 */
export function generateMultipleFormsMessage(
  patient: FormMessagePatient,
  forms: FormTable[],
  tenantSubDomain?: string,
  appointmentId?: string,
  membershipId?: string,
): string {
  if (!tenantSubDomain) {
    return `Hi ${patient.firstName}, we don't currently have any forms available to send.`;
  }

  if (forms.length === 0) {
    return `Hi ${patient.firstName}, we don't currently have any forms available to send.`;
  }

  // Outside an appointment, a single form keeps its direct form URL. An
  // appointment selection must keep the appointment URL even when it only has
  // one form because that URL carries the appointment context.
  if (forms.length === 1 && !appointmentId) {
    return generateFormMessage(
      patient,
      forms[0],
      tenantSubDomain,
      membershipId,
    );
  }

  const formNames = forms.map((form) => form.name);
  const formsUrl = appointmentId
    ? createFormLink(tenantSubDomain, appointmentId)
    : buildFormSuiteUrl(
        tenantSubDomain,
        forms.map((form) => form.pathName),
        membershipId,
      );
  const sharedTemplate = forms[0].defaultSendingText;

  // A suite has one link, so a configured template can represent it once when
  // every selected form uses that same template. Different templates cannot be
  // merged safely and fall back to the deterministic suite message below.
  if (
    sharedTemplate &&
    forms.every((form) => form.defaultSendingText === sharedTemplate)
  ) {
    return renderFormMessageTemplate(
      sharedTemplate,
      patient,
      formNames.join(", "),
      formsUrl,
    );
  }

  if (appointmentId) {
    return `Hi ${patient.firstName},

Please complete your appointment forms using the link below:

${formsUrl}

Thank you!`;
  }

  return `Hi ${patient.firstName},

Please complete these ${forms.length} forms:

${formNames.map((name, index) => `${index + 1}. ${name}`).join("\n")}

${formsUrl}

Thank you!`;
}
